import { DataService } from './data-service';
import { logApiResponse, validateBulkSearchResponse, logBulkSearchResults } from '../utils/api-debug';

export interface BulkBarcodeSearchRequest {
  barcodes: string[];
}

export interface LocationAggregation {
  storageUnitId: string;
  storageUnitName: string;
  storageUnitCode: string;
  boxes: {
    boxBarcode: string;
    quantity: number;
  }[];
  independentQuantity?: number;
  totalQuantity: number;
  availableQuantity: number;
  itemName?: string;
  unitId?: string;
}

export interface BarcodeSearchResult {
  type: string; // "box" or "unit"
  locations: LocationAggregation[];
  unit: any;
  itemName: string;
  unitId?: string;
}

export interface BulkBarcodeSearchItem {
  barcode: string;
  result?: BarcodeSearchResult;
  error?: string;
}

export interface BulkBarcodeSearchResponse {
  success: boolean;
  message: string;
  data: {
    results: BulkBarcodeSearchItem[];
  };
}

export interface BulkBarcodeSearchResult {
  result: any;
  barcode: string;
  found: boolean;
  type?: 'box' | 'unit';
  itemName?: string;
  unit: any;
  unitId?: string;
  locations?: LocationAggregation[];
}

class BulkBarcodeService {
  private cache = new Map<string, BulkBarcodeSearchResult>();
  private readonly CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 1000;
  private cacheTimestamps = new Map<string, number>();

  /**
   * Search for multiple barcodes in bulk with caching and performance optimizations
   */
  async searchBarcodes(
    barcodes: string[],
    options: { includeLocations?: boolean; includeBoxes?: boolean } = {}
  ): Promise<BulkBarcodeSearchResult[]> {
    // Remove duplicates and empty barcodes
    const uniqueBarcodes = [...new Set(barcodes.filter(b => b && b.trim()))];
    
    if (uniqueBarcodes.length === 0) {
      return [];
    }

    // Check cache first and separate cached vs uncached barcodes
    const cachedResults: BulkBarcodeSearchResult[] = [];
    const uncachedBarcodes: string[] = [];
    const now = Date.now();

    for (const barcode of uniqueBarcodes) {
      const cacheKey = this.getCacheKey(barcode, options);
      const cachedResult = this.cache.get(cacheKey);
      const cacheTime = this.cacheTimestamps.get(cacheKey);

      if (cachedResult && cacheTime && (now - cacheTime < this.CACHE_EXPIRY)) {
        cachedResults.push(cachedResult);
      } else {
        uncachedBarcodes.push(barcode);
        // Clean up expired cache entry
        if (cachedResult) {
          this.cache.delete(cacheKey);
          this.cacheTimestamps.delete(cacheKey);
        }
      }
    }

    // If all results are cached, return immediately
    if (uncachedBarcodes.length === 0) {
      return cachedResults;
    }

    // Process all uncached barcodes together (no batching)
    const newResults: BulkBarcodeSearchResult[] = [];

    if (uncachedBarcodes.length > 0) {
      try {
        const request: BulkBarcodeSearchRequest = {
          barcodes: uncachedBarcodes
        };

        const response = await DataService.post('api/inventoryItem/bulk-search-barcode', request);
        
        if (!response.ok) {
          const fallbackResults = await this.fallbackToIndividualSearch(uncachedBarcodes, options);
          newResults.push(...fallbackResults);
        } else {
          const result: BulkBarcodeSearchResponse = await response.json();
          
          // Debug logging
          logApiResponse('bulk-search-barcode', request, result);
          
          // Validate response structure
          if (!validateBulkSearchResponse(result)) {
            console.warn('⚠️ Invalid bulk search response, falling back to individual searches');
            const fallbackResults = await this.fallbackToIndividualSearch(uncachedBarcodes, options);
            newResults.push(...fallbackResults);
          } else if (result.success && result.data.results) {
            const transformedResults = result.data.results.map(item => {
              const transformedResult: BulkBarcodeSearchResult = {
                barcode: item.barcode,
                found: !!item.result && !item.error,
                type: item.result?.type === 'box' ? 'box' : 'unit',
                itemName: item.result?.itemName,
                unitId: item.result?.unitId,
                locations: item.result?.locations || [],
                result: item.result,
                unit: item.result?.unit || item.result
              };
              
              // Cache the result
              const cacheKey = this.getCacheKey(item.barcode, options);
              this.setCacheEntry(cacheKey, transformedResult);
              
              return transformedResult;
            });
            
            newResults.push(...transformedResults);
          } else {
            // API returned success: false, fall back to individual searches
            const fallbackResults = await this.fallbackToIndividualSearch(uncachedBarcodes, options);
            newResults.push(...fallbackResults);
          }
        }
      } catch (error) {
        console.error('Bulk barcode search failed:', error);
        // Fall back to individual searches
        const fallbackResults = await this.fallbackToIndividualSearch(uncachedBarcodes, options);
        newResults.push(...fallbackResults);
      }
    }

    // Combine cached and new results
    const finalResults = [...cachedResults, ...newResults];
    
    // Debug logging for final results
    logBulkSearchResults(uniqueBarcodes, finalResults);
    
    return finalResults;
  }

  /**
   * Cache management helper methods
   */
  private getCacheKey(barcode: string, options: { includeLocations?: boolean; includeBoxes?: boolean }): string {
    return `${barcode}:${options.includeLocations}:${options.includeBoxes}`;
  }

  private setCacheEntry(cacheKey: string, result: BulkBarcodeSearchResult): void {
    // Implement LRU cache eviction if cache is getting too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
        this.cacheTimestamps.delete(oldestKey);
      }
    }
    
    this.cache.set(cacheKey, result);
    this.cacheTimestamps.set(cacheKey, Date.now());
  }

  /**
   * Fallback to individual barcode searches if bulk API fails
   */
  private async fallbackToIndividualSearch(
    barcodes: string[],
    options: { includeLocations?: boolean; includeBoxes?: boolean }
  ): Promise<BulkBarcodeSearchResult[]> {
    const results: BulkBarcodeSearchResult[] = [];
    
    // Process individual searches with delay to avoid overwhelming the server
    for (const barcode of barcodes) {
      try {
        const response = await DataService.get(`api/InventoryItem/search-barcode/${encodeURIComponent(barcode)}`);
        
        if (response.ok) {
          const result = await response.json();
          
          const searchResult: BulkBarcodeSearchResult = result.success && result.data ? {
            barcode,
            found: true,
            type: result.data.type === 'box' ? 'box' : 'unit',
            itemName: result.data.itemName || '',
            unitId: result.data.unitId,
            locations: result.data.locations || [],
            unit: result.data,
            result: result.data
          } : {
            barcode,
            found: false,
            result: undefined,
            unit: undefined
          };

          // Cache the result
          const cacheKey = this.getCacheKey(barcode, options);
          this.setCacheEntry(cacheKey, searchResult);
          
          results.push(searchResult);
        } else {
          const searchResult: BulkBarcodeSearchResult = {
            barcode,
            found: false,
            unit: undefined,
            result: undefined
          };
          
          // Cache the negative result too (for a shorter time)
          const cacheKey = this.getCacheKey(barcode, options);
          this.setCacheEntry(cacheKey, searchResult);
          
          results.push(searchResult);
        }
      } catch (error) {
        console.error(`Failed to search barcode ${barcode}:`, error);
        const searchResult: BulkBarcodeSearchResult = {
          barcode,
          found: false,
          unit: undefined,
          result: undefined
        };
        
        // Cache the error result
        const cacheKey = this.getCacheKey(barcode, options);
        this.setCacheEntry(cacheKey, searchResult);
        
        results.push(searchResult);
      }

      // Small delay between requests to be nice to the server
      if (barcodes.indexOf(barcode) < barcodes.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    return results;
  }

  /**
   * Match location names with imported data
   */
  matchLocationByName(
    searchResults: BulkBarcodeSearchResult[],
    importedLocationName?: string
  ): { storageUnitId: string; storageUnitName: string } | null {
    if (!importedLocationName || !searchResults) return null;

    const normalizedImported = importedLocationName.toLowerCase().trim();

    for (const result of searchResults) {
      if (!result.found || !result.locations || !Array.isArray(result.locations)) continue;

      for (const location of result.locations) {
        if (!location || !location.storageUnitName) continue;
        
        const normalizedLocation = location.storageUnitName.toLowerCase().trim();
        
        // Exact match
        if (normalizedLocation === normalizedImported) {
          return {
            storageUnitId: location.storageUnitId,
            storageUnitName: location.storageUnitName
          };
        }

        // Partial match
        if (normalizedLocation.includes(normalizedImported) || 
            normalizedImported.includes(normalizedLocation)) {
          return {
            storageUnitId: location.storageUnitId,
            storageUnitName: location.storageUnitName
          };
        }
      }
    }

    return null;
  }

  /**
   * Match box barcode with imported data
   */
  matchBoxByBarcode(
    searchResult: BulkBarcodeSearchResult,
    importedBoxBarcode?: string
  ): { boxBarcode: string; quantity: number } | null {
    if (!importedBoxBarcode || !searchResult.found || !searchResult.locations || !Array.isArray(searchResult.locations)) {
      return null;
    }

    const normalizedImported = importedBoxBarcode.toLowerCase().trim();

    for (const location of searchResult.locations) {
      if (!location || !location.boxes || !Array.isArray(location.boxes)) continue;
      
      for (const box of location.boxes) {
        if (!box || !box.boxBarcode) continue;
        
        if (box.boxBarcode.toLowerCase().trim() === normalizedImported) {
          return {
            boxBarcode: box.boxBarcode,
            quantity: box.quantity || 0
          };
        }
      }
    }

    return null;
  }

  /**
   * Clear the cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheTimestamps.clear();
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): { size: number; maxSize: number; hitRate?: number } {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE
    };
  }
}

export const bulkBarcodeService = new BulkBarcodeService();